/**
 * PlayerPlane.ts — The player's bush plane.
 *
 * Handles:
 * - Construction of a stylised low-poly airplane mesh
 * - Arcade flight physics (lift, drag, gravity, throttle)
 * - Ground collision / landing detection
 * - Particle trail when flying fast
 */
import * as THREE from 'three';
/* ========================================================
   CONSTANTS
   ======================================================== */
const GRAVITY = 20;
const MAX_SPEED = 160;
const MIN_LIFT_SPEED = 18;
const LIFT_FACTOR = 6.0; // lift = gravity at ~48 speed; plane takes off before ground-drag terminal (~77)
const MAX_LIFT = GRAVITY * 2.5; // cap so high-speed doesn't make the plane uncontrollable
const DRAG_FACTOR = 0.6;
const GROUND_DRAG = 1.5; // low enough that full-throttle roll reaches takeoff speed
const PITCH_SPEED = 0.8;
const ROLL_SPEED = 2.8;
const YAW_SPEED = 1.0;
const THROTTLE_ACCEL = 0.6;
export class PlayerPlane {
    constructor(game) {
        this.velocity = new THREE.Vector3();
        this.speed = 0; // scalar forward speed
        this.throttle = 0.5; // 0‥1
        this.gearDown = true;
        this.onGround = false;
        this.trailIdx = 0;
        this.TRAIL_COUNT = 200;
        this.game = game;
        this.mesh = new THREE.Group();
        this.buildModel();
        this.buildTrail();
        this.mesh.position.set(0, 2, 0);
        game.scene.add(this.mesh);
    }
    /* ================= MODEL ================= */
    buildModel() {
        const mat = new THREE.MeshStandardMaterial({ color: 0xdddddd, flatShading: true });
        const matR = new THREE.MeshStandardMaterial({ color: 0xcc3333, flatShading: true });
        const matD = new THREE.MeshStandardMaterial({ color: 0x333333, flatShading: true });
        const matG = new THREE.MeshStandardMaterial({ color: 0xffd500, flatShading: true });
        // Fuselage
        const fuse = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.2, 5), mat);
        this.mesh.add(fuse);
        // Cockpit
        const cockpit = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.7, 1.6), new THREE.MeshStandardMaterial({ color: 0x5588cc, flatShading: true, transparent: true, opacity: 0.7 }));
        cockpit.position.set(0, 0.8, -0.3);
        this.mesh.add(cockpit);
        // Wings
        const wingGeo = new THREE.BoxGeometry(9, 0.15, 1.6);
        const wingL = new THREE.Mesh(wingGeo, mat);
        wingL.position.set(0, 0.1, 0);
        this.mesh.add(wingL);
        // Wing struts
        const strutGeo = new THREE.BoxGeometry(0.1, 0.6, 0.1);
        for (const sx of [-3, 3]) {
            const s = new THREE.Mesh(strutGeo, matD);
            s.position.set(sx, -0.3, 0);
            this.mesh.add(s);
        }
        // Tail vertical
        const tailV = new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.2, 0.9), matR);
        tailV.position.set(0, 0.8, 2.3);
        this.mesh.add(tailV);
        // Tail horizontal
        const tailH = new THREE.Mesh(new THREE.BoxGeometry(3, 0.1, 0.7), mat);
        tailH.position.set(0, 0.35, 2.3);
        this.mesh.add(tailH);
        // Engine cowl
        const cowl = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.65, 0.8, 6), matD);
        cowl.rotation.x = Math.PI / 2;
        cowl.position.set(0, 0, -2.9);
        this.mesh.add(cowl);
        // Propeller
        const propGeo = new THREE.BoxGeometry(3.2, 0.18, 0.12);
        this.propeller = new THREE.Mesh(propGeo, matG);
        this.propeller.position.set(0, 0, -3.35);
        this.mesh.add(this.propeller);
        // Landing gear legs
        const legGeo = new THREE.BoxGeometry(0.1, 0.7, 0.1);
        for (const sx of [-1, 1]) {
            const leg = new THREE.Mesh(legGeo, matD);
            leg.position.set(sx, -0.95, -0.6);
            this.mesh.add(leg);
            const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.12, 8), matD);
            wheel.rotation.z = Math.PI / 2;
            wheel.position.set(sx, -1.35, -0.6);
            this.mesh.add(wheel);
        }
        // Tail wheel
        const tw = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.08, 8), matD);
        tw.rotation.z = Math.PI / 2;
        tw.position.set(0, -0.95, 2.1);
        this.mesh.add(tw);
        this.mesh.castShadow = true;
        this.mesh.traverse((c) => { if (c.isMesh) {
            c.castShadow = true;
            c.receiveShadow = true;
        } });
    }
    /* ================= PARTICLE TRAIL ================= */
    buildTrail() {
        this.trailPositions = new Float32Array(this.TRAIL_COUNT * 3);
        this.trailGeo = new THREE.BufferGeometry();
        this.trailGeo.setAttribute('position', new THREE.BufferAttribute(this.trailPositions, 3));
        const trailMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.35, transparent: true, opacity: 0.4, depthWrite: false });
        this.trailParticles = new THREE.Points(this.trailGeo, trailMat);
        this.trailParticles.frustumCulled = false;
        this.game.scene.add(this.trailParticles);
    }
    /* ================= UPDATE ================= */
    update(dt) {
        const ctrl = this.game.controls;
        /* Throttle */
        this.throttle = THREE.MathUtils.clamp(this.throttle + ctrl.throttleInput * THROTTLE_ACCEL * dt, 0, 1);
        /* Rotation inputs (applied in local space) */
        const pitchAmt = ctrl.pitchInput * PITCH_SPEED * dt;
        const rollAmt = ctrl.rollInput * ROLL_SPEED * dt;
        const yawAmt = ctrl.yawInput * YAW_SPEED * dt;
        const q = this.mesh.quaternion;
        const pitchQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), pitchAmt);
        const rollQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), rollAmt);
        const yawQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawAmt);
        q.multiply(pitchQ).multiply(rollQ).multiply(yawQ);
        /* Auto-level: gently correct roll and pitch toward wings-level when requested */
        if (ctrl.autoLevel > 0 && !this.onGround) {
            const euler = new THREE.Euler().setFromQuaternion(q, 'YXZ');
            const strength = ctrl.autoLevel * 1.2 * dt;
            euler.z = THREE.MathUtils.lerp(euler.z, 0, strength); // level roll only — no pitch correction
            q.setFromEuler(euler);
        }
        /* Forward direction */
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(q);
        const up = new THREE.Vector3(0, 1, 0).applyQuaternion(q);
        /* Engine force */
        const engineForce = forward.clone().multiplyScalar(this.throttle * MAX_SPEED * 0.7);
        /* Lift (proportional to speed², acts along local up, capped so high-speed stays controllable) */
        const speedFactor = Math.min(this.speed / MIN_LIFT_SPEED, 1);
        const liftMag = Math.min(speedFactor * speedFactor * LIFT_FACTOR * this.speed * 0.07, MAX_LIFT);
        const lift = up.clone().multiplyScalar(liftMag);
        /* Drag (opposes velocity) */
        const drag = this.velocity.clone().multiplyScalar(-DRAG_FACTOR * dt);
        /* Gravity */
        const gravity = new THREE.Vector3(0, -GRAVITY, 0).multiplyScalar(dt);
        /* Wind */
        const wind = this.game.windDir.clone().multiplyScalar(this.game.windStrength * dt);
        /* Accumulate forces */
        this.velocity.add(engineForce.multiplyScalar(dt));
        this.velocity.add(lift.multiplyScalar(dt));
        this.velocity.add(drag);
        this.velocity.add(gravity);
        this.velocity.add(wind);
        /* Brake */
        if (ctrl.brake && this.onGround) {
            this.velocity.multiplyScalar(1 - 6 * dt);
        }
        /* Recovery key (R) — ONE-SHOT: teleports plane 20 m above terrain, levels it, kicks it forward */
        if (ctrl.recover) {
            ctrl.recover = false; // consume immediately so it never fires twice
            const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.mesh.quaternion);
            fwd.y = 0;
            fwd.normalize();
            const recoverY = this.game.world.getTerrainHeight(this.mesh.position.x, this.mesh.position.z) + 22;
            this.mesh.position.y = Math.max(this.mesh.position.y + 0.1, recoverY);
            // Launch well above takeoff speed (48) so lift immediately overcomes gravity
            this.velocity.set(fwd.x * 60, 22, fwd.z * 60);
            this.throttle = 1.0;
            // Hard-level the plane so all thrust is horizontal
            this.mesh.quaternion.slerp(new THREE.Quaternion(), 0.8);
            this.onGround = false;
        }
        /* Ground collision */
        const terrainY = this.game.world.getTerrainHeight(this.mesh.position.x, this.mesh.position.z);
        const groundY = terrainY + 1.6; // wheel clearance
        /* Slope-ahead detection: sample terrain 3 m in front and push up if rising */
        const aheadPos = this.mesh.position.clone().addScaledVector(new THREE.Vector3(0, 0, -1).applyQuaternion(this.mesh.quaternion), 3);
        const terrainAhead = this.game.world.getTerrainHeight(aheadPos.x, aheadPos.z);
        const slopeRise = terrainAhead - terrainY;
        if (this.onGround && slopeRise > 0.3) {
            // Push the plane up the slope at the rate of the rise so it doesn't snag
            this.velocity.y += slopeRise * 14 * dt;
        }
        if (this.mesh.position.y <= groundY) {
            this.mesh.position.y = groundY;
            if (this.velocity.y < 0)
                this.velocity.y = 0;
            /* Only auto-correct roll while on the ground, never pitch.
               Pitch must remain free so the player can rotate nose up and take off. */
            const euler = new THREE.Euler().setFromQuaternion(this.mesh.quaternion, 'YXZ');
            euler.x = THREE.MathUtils.lerp(euler.x, 0, 1.5 * dt); // gentle pitch-toward-0 only when near ground
            euler.z = THREE.MathUtils.lerp(euler.z, 0, 4.0 * dt); // correct roll fairly quickly
            this.mesh.quaternion.setFromEuler(euler);
            // Reduce friction when throttle is applied so the plane can accelerate to takeoff speed
            const frictionScale = 1 - GROUND_DRAG * (1 - this.throttle * 0.7) * dt;
            this.velocity.x *= frictionScale;
            this.velocity.z *= frictionScale;
            this.onGround = true;
        }
        else {
            this.onGround = false;
        }
        /* Integrate position */
        this.mesh.position.add(this.velocity.clone().multiplyScalar(dt));
        /* Clamp world bounds (large but finite) */
        const B = 2400;
        this.mesh.position.x = THREE.MathUtils.clamp(this.mesh.position.x, -B, B);
        this.mesh.position.z = THREE.MathUtils.clamp(this.mesh.position.z, -B, B);
        if (this.mesh.position.y > 1200) {
            this.mesh.position.y = 1200;
            this.velocity.y = 0;
        }
        /* Scalar speed for HUD etc. */
        this.speed = this.velocity.length();
        /* Spin propeller */
        this.propeller.rotation.z += (this.throttle * 60 + 5) * dt;
        /* Update trail particles (only when moving fast) */
        if (this.speed > 30) {
            const i = this.trailIdx % this.TRAIL_COUNT;
            const p = this.mesh.position;
            this.trailPositions[i * 3] = p.x + (Math.random() - 0.5) * 2;
            this.trailPositions[i * 3 + 1] = p.y + (Math.random() - 0.5) * 2;
            this.trailPositions[i * 3 + 2] = p.z + (Math.random() - 0.5) * 2;
            this.trailGeo.attributes.position.needsUpdate = true;
            this.trailIdx++;
        }
    }
}
